<template>
    <div v-if="forecast" class="wrapper">
        <DefaultButton @click="$router.push(`${ROUTES.BASE}/${forecastId}`)" style="margin-bottom: 10px">&laquo; Back</DefaultButton>
        <ForecastNameBanner :forecast="forecast" />
        <ForecastInfos style="padding: 10px 0;" :forecast="forecast" />
        <CalendarInfo :forecast="forecast" />
        <Comments :forecast="forecast" />
        <h1 class="title SectionBorderTop">Secondary Window Dates</h1>
        <loader :loading="isLoading" v-if="isLoading"></loader>
        <div class="row">
            <div class="col-sm-1"></div>
            <div class="col-sm-2">Start date*</div>
            <div class="col-sm-2">End date*</div>
            <div class="col-sm-1">Reminder*</div>
            <div class="col-sm-3">Message / Recommendations</div>
            <div class="col-sm-1"></div>
            <div class="col-sm-2"></div>
        </div>
        <div class="forecast-windows">
            <SecondaryWindowForm
                :forecast-window="defaultForecastWindow"
                :min="forecast.startDate"
                :max="forecast.endDate"
                :is-default="true"
                :is-apply-disabled="selectedForecastWindows.length === 0"
                @applyDefault="applyDefault"
            />
        </div>
        <div class="row actions">
            <div class="col-sm-1"></div>
            <div class="col-sm-2"></div>
            <div class="col-sm-2"></div>
            <div class="col-sm-1"></div>
            <div class="col-sm-3"></div>
            <div class="col-sm-1" style="margin-top: auto; text-align: center">
                <ClientColorButton :size="'xs'" @click="selectAll" :disabled="selectedForecastWindows.length === forecastWindows.length"
                    >Select All</ClientColorButton
                >
            </div>
            <div class="col-sm-2">
                <ClientColorButton @click="publishSelected" style="width: 100%" :disabled="selectedForecastWindows.length === 0"
                    >Publish selected</ClientColorButton
                >
            </div>
        </div>
        <div v-for="(fcw, index) in forecastWindows" :key="fcw.id" class="forecast-windows">
            <SecondaryWindowForm
                :forecast-window="fcw"
                :min="forecast.startDate"
                :max="forecast.endDate"
                :index="index"
                @published="fetchForecastWindows"
                @toggleSelect="toggleSelect(fcw)"
                :is-selected="selectedForecastWindows.some(fw => fw.id === fcw.id)"
                :is-valid="isValid(fcw)"
            />
        </div>
    </div>
</template>
<script>
import forecastsService from "@/components/forecasts/forecastsService";
import loader from "@/components/loader/loader";
import { mapActions } from "vuex";
import SecondaryWindowForm from "@/components/forecasts/elements/form/SecondaryWindowForm";
import Diffusion from "@/components/modal/Diffusion";
import { ROUTES } from "@/components/forecasts/router";
import DefaultButton from "@/components/forecasts/elements/form/DefaultButton";
import ForecastNameBanner from "@/components/forecasts/elements/general/ForecastNameBanner";
import ForecastInfos from "@/components/forecasts/elements/general/ForecastInfos";
import CalendarInfo from "@/components/forecasts/pages/sections/CalendarInfo";
import Comments from "@/components/forecasts/pages/sections/Comments";
import Globals from "@/components/globals";

export default {
    components: {
        Comments,
        CalendarInfo,
        ForecastInfos,
        ForecastNameBanner,
        DefaultButton,
        Diffusion,
        SecondaryWindowForm,
        loader
    },
    data() {
        return {
            forecastId: null,
            forecastWindows: [],
            selectedForecastWindows: [],
            items: [],
            isLoading: false,
            ROUTES,
            defaultForecastWindow: {
                startDate: null,
                endDate: null,
                daysBeforeNotification: 5,
                supervisorComment: ""
            }
        };
    },
    async created() {
        this.isLoading = true;
        await this.populate();
        this.forecastId = this.$route.params.id;
        await this.fetchForecastWindows();

        // Rediriger si le forecast a été publié en mode "all zones"
        if (this.forecast && this.forecast.publishMode === "all_zones") {
            this.$router.push(`${ROUTES.BASE}/${this.forecastId}`);
            return;
        }

        this.defaultForecastWindow.startDate = this.forecast.startDate;
        this.defaultForecastWindow.endDate = this.forecast.endDate;
        this.isLoading = false;
    },
    computed: {
        forecast() {
            // it's very important to call this getter in a computed property to make sure the getter in the store stays reactive
            // see https://forum.vuejs.org/t/how-to-make-vuex-getters-which-return-function-reactive/28949
            return this.$store.getters["forecast/getForecast"](this.forecastId);
        }
    },
    methods: {
        ...mapActions("forecast", { populate: "populate" }),
        async fetchForecastWindows() {
            this.isLoading = true;
            this.forecastWindows = [];
            this.selectedForecastWindows = [];
            const forecastWindows = await forecastsService.getForecastWindowsForForecast(this.forecastId);
            // En mode "affiliate only" (ou legacy publishMode null), les zones affiliate (non multivalidation)
            // sont déjà publiées au lancement : la page de gestion des publications ne doit lister que les
            // zones Export (multivalidation) restant à gérer. Seul le mode "all zones" affiche toutes les zones.
            this.forecastWindows =
                this.forecast && this.forecast.publishMode !== "all_zones" ? forecastWindows.filter(fcw => fcw.multiValidation) : forecastWindows;
            this.isLoading = false;
        },
        isDateValid(date) {
            if (!date instanceof Date) {
                return false;
            }
            let tempDate = Globals.moment(date);
            return tempDate.isValid();
        },
        applyDefault() {
            this.selectedForecastWindows.map(fcw => {
                if (this.isDateValid(this.defaultForecastWindow.startDate)) {
                    fcw.startDate = this.defaultForecastWindow.startDate;
                }
                if (this.isDateValid(this.defaultForecastWindow.endDate)) {
                    fcw.endDate = this.defaultForecastWindow.endDate;
                }
                fcw.daysBeforeNotification = this.defaultForecastWindow.daysBeforeNotification;
                fcw.supervisorComment = this.defaultForecastWindow.supervisorComment;

                return fcw;
            });
        },
        toggleSelect(forecastWindow) {
            const isSelected = this.selectedForecastWindows.some(fcw => forecastWindow.id === fcw.id);
            if (isSelected) {
                this.selectedForecastWindows = this.selectedForecastWindows.filter(fcw => forecastWindow.id !== fcw.id);
            } else {
                this.selectedForecastWindows.push(forecastWindow);
            }
        },
        selectAll() {
            this.selectedForecastWindows = [];
            this.selectedForecastWindows = this.forecastWindows;
        },
        isValid(forecastWindow) {
            if (!forecastWindow.daysBeforeNotification || forecastWindow.daysBeforeNotification === "") return false;
            if (!this.isDateValid(forecastWindow.startDate)) return false;
            if (!this.isDateValid(forecastWindow.endDate)) return false;
            if (forecastWindow.startDate > forecastWindow.endDate) return false;

            return true;
        },
        async publishSelected() {
            const text = this.isUpdate
                ? "Do you really want to republish the selected secondary windows?"
                : "Do you really want to publish the selected secondary window?";
            const buttonText = this.isUpdate ? "Republish" : "Publish";
            const res = await this.dialogConfirm(text, buttonText, "Cancel");
            if (res === true) {
                this.isLoading = true;
                try {
                    let requests = [];
                    for (let fw of this.selectedForecastWindows) {
                        if (this.isValid(fw)) {
                            requests.push(forecastsService.updateForecastSecondaryWindow(fw));
                        }
                    }
                    Promise.all(requests)
                        .then(() => {
                            this.dialogSuccess("Successfully (re)published");
                            this.isLoading = false;
                        })
                        .catch(e => {
                            if (e.response && e.response.data) {
                                this.dialogError(e.response.data.join("\n"));
                            } else {
                                this.dialogError(e);
                            }
                            this.isLoading = false;
                        });
                } catch (e) {
                    this.dialogError("An error occurred");
                    this.isLoading = false;
                }
            }
        }
    }
};
</script>
<style scoped lang="scss">
@import "../css/forecasts";
.wrapper {
    width: 100%;
    text-transform: uppercase;
}
.forecast-windows {
    padding: 10px;
}
</style>



// WEBPACK FOOTER //
// ForecastSecondaryWindow.vue?7b3129fa